import { useCallback, useEffect, useState, type ReactElement } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { wellPoSpendUrl } from '../../constants';
import { formatMoney } from '../../domain/formatters';
import type { WellPoSpendGranularity, WellPoSpendPoint, WellPoSpendSeries } from '../../domain/wells';

interface WellPoSpendChartsProps {
  wellId: string;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
}

function normalizeSeries(raw: unknown): WellPoSpendSeries | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const pointsRaw = o.points;
  if (!Array.isArray(pointsRaw)) {
    return null;
  }
  const points: WellPoSpendPoint[] = pointsRaw.map((p) => {
    const r = p as Record<string, unknown>;
    return {
      label: String(r.label ?? ''),
      periodKey: String(r.periodKey ?? ''),
      totalAmount: Number(r.totalAmount ?? 0),
      purchaseOrderCount: Number(r.purchaseOrderCount ?? 0)
    };
  });
  return {
    granularity: String(o.granularity ?? ''),
    calendarYear: o.calendarYear === null || o.calendarYear === undefined ? null : Number(o.calendarYear),
    points
  };
}

function SpendTooltip({
  active,
  payload
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: WellPoSpendPoint }>;
}): ReactElement | null {
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }
  return (
    <div className="well-po-chart-tooltip">
      <div className="well-po-chart-tooltip-label">{row.label}</div>
      <div>{formatMoney(row.totalAmount)}</div>
      <div className="well-po-chart-tooltip-meta">{row.purchaseOrderCount} PO(s)</div>
    </div>
  );
}

function CountTooltip({
  active,
  payload
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: WellPoSpendPoint }>;
}): ReactElement | null {
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }
  return (
    <div className="well-po-chart-tooltip">
      <div className="well-po-chart-tooltip-label">{row.label}</div>
      <div>{row.purchaseOrderCount} purchase order(s)</div>
      <div className="well-po-chart-tooltip-meta">{formatMoney(row.totalAmount)} total</div>
    </div>
  );
}

export function WellPoSpendCharts({ wellId, authFetch }: WellPoSpendChartsProps): ReactElement {
  const [granularity, setGranularity] = useState<WellPoSpendGranularity>('monthly');
  const [series, setSeries] = useState<WellPoSpendSeries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(wellPoSpendUrl(wellId, granularity));
      if (!response.ok) {
        const text = await response.text();
        setError(text || 'Could not load PO spend.');
        setSeries(null);
        return;
      }
      const json: unknown = await response.json();
      const normalized = normalizeSeries(json);
      setSeries(normalized);
    } catch {
      setError('Could not load PO spend.');
      setSeries(null);
    } finally {
      setLoading(false);
    }
  }, [authFetch, wellId, granularity]);

  useEffect(() => {
    void load();
  }, [load]);

  const chartData = series?.points ?? [];
  const yearHint =
    series?.calendarYear != null && (granularity === 'monthly' || granularity === 'quarterly')
      ? `Calendar year ${series.calendarYear} (UTC).`
      : granularity === 'yearly'
        ? 'One bucket per calendar year (UTC), from PO created date.'
        : null;

  return (
    <section className="well-po-charts" aria-label="Purchase order spend for this well">
      <div className="well-po-charts-toolbar">
        <h4 className="well-po-charts-title">Purchase order activity</h4>
        <label className="well-po-charts-granularity">
          <span className="sr-only">Time period</span>
          <select
            value={granularity}
            onChange={(e) => setGranularity(e.target.value as WellPoSpendGranularity)}
          >
            <option value="yearly">Yearly</option>
            <option value="monthly">Monthly (current year)</option>
            <option value="quarterly">Quarterly (current year)</option>
          </select>
        </label>
      </div>
      {yearHint && <p className="hint well-po-charts-hint">{yearHint}</p>}
      {loading && <p className="hint">Loading charts…</p>}
      {error && !loading && <p className="error">{error}</p>}
      {!loading && !error && chartData.length === 0 && (
        <p className="hint">No purchase orders linked to this well.</p>
      )}
      {!loading && !error && chartData.length > 0 && (
        <div className="well-po-charts-grid">
          <div className="well-po-chart-card">
            <h5 className="well-po-chart-heading">PO spend</h5>
            <div className="well-po-chart-frame">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-35} textAnchor="end" height={52} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) =>
                      new Intl.NumberFormat(undefined, {
                        notation: 'compact',
                        maximumFractionDigits: 1
                      }).format(v)
                    }
                  />
                  <Tooltip content={<SpendTooltip />} />
                  <Line type="monotone" dataKey="totalAmount" stroke="#155eef" strokeWidth={2} dot={{ r: 3 }} name="Spend" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="well-po-chart-card">
            <h5 className="well-po-chart-heading">PO count</h5>
            <div className="well-po-chart-frame well-po-chart-frame--bar">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-35} textAnchor="end" height={52} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CountTooltip />} />
                  <Bar dataKey="purchaseOrderCount" fill="#0f766e" radius={[4, 4, 0, 0]} name="POs" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
